import type { ReactNode } from 'react';

import { Textarea } from '@web/ui/core/atoms';

import { FieldHint } from './field_hint';
import { FieldLabel } from './field_label';

type FormTextAreaFieldProps = {
  field: {
    name: string;
    state: { value: unknown; meta: { errors: unknown[]; isTouched: boolean } };
    handleBlur: () => void;
    handleChange: (value: string) => void;
  };
  label: ReactNode;
  required?: boolean;
  labelEndAdornment?: ReactNode;
  hint?: ReactNode;
  showErrorWhen?: boolean;

  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  rows?: number;
};

/*
 * TanStack Form-bound textarea. Mirrors FormTextField's scaffolding around
 * the DS <Textarea> primitive.
 */
export function FormTextAreaField(props: Readonly<FormTextAreaFieldProps>): React.JSX.Element {
  const {
    field,
    label,
    required,
    labelEndAdornment,
    hint,
    showErrorWhen,
    placeholder,
    autoFocus,
    autoComplete,
    spellCheck,
    disabled,
    readOnly,
    rows,
  } = props;

  const firstError = field.state.meta.errors[0];
  const rawError = typeof firstError === 'string' ? firstError : undefined;
  const isTouched = field.state.meta.isTouched;
  const showError = showErrorWhen ?? (isTouched && !!rawError);
  const errorText = showError ? rawError : undefined;

  return (
    <div className="mb-4.5 flex flex-col gap-1">
      <FieldLabel htmlFor={field.name} required={required} endAdornment={labelEndAdornment}>
        {label}
      </FieldLabel>
      <Textarea
        id={field.name}
        name={field.name}
        value={typeof field.state.value === 'string' ? field.state.value : ''}
        onBlur={field.handleBlur}
        onChange={(e) => {
          field.handleChange(e.target.value);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows ?? 3}
        aria-invalid={showError || undefined}
      />
      <FieldHint hint={hint} error={errorText} />
    </div>
  );
}
